/**
 * MetricsCalculator.calculate (CONVENTIONS §5.6): the summary figures shown beside a forecast.
 * Current state comes from the transactions at the clock (or the first buckets without them);
 * finals, peaks and lows from the cumulative series; totals from the cashflow.
 */
import { addDays, addYears, startOfMonth, type Day } from '../core/calendar.ts';
import {
  abs, add, dec, div, fromInt, gt, isZero, lt, mul, round2, sub, sum, ZERO, type Decimal,
} from '../core/decimal.ts';
import { assetFromDepreciatingAsset, assetValue, defaultsToAppreciation } from '../models/asset.ts';
import { reamortisedMortgageBalance } from '../models/mortgage.ts';
import { isOwnedOn, isSoldBy } from '../models/plan.ts';
import { propertyMonthlyCosts } from '../models/property.ts';
import { paidOn } from '../models/transaction.ts';
import type {
  Asset, CalcContext, DepreciatingAsset, Id, Property, Series, Transaction, TransactionType,
} from '../types/domain.ts';
import { appreciatedValue } from './appreciation.ts';
import type { CashflowType } from './cashflow.ts';
import { firstClearBucket, remainingBalance, totalCurrentDebt, totalPaid } from './debt-projector.ts';
import { calculateTotalCurrentInvestments } from './investment-projector.ts';
import { runPot } from './pot.ts';
import { countsAsDebt } from './net-worth.ts';
import { countOccurrencesUpTo, occurrenceDate } from './occurrences.ts';
import { saleDates, saleSettlements, settleSoldAssetDebts } from './sales.ts';

export type CumulativeSeriesName = 'cash' | 'investment' | 'equity' | 'debt' | 'netWorth' | 'passive';

/** CumulativeForecast.allCases order. */
export const CUMULATIVE_ORDER: readonly CumulativeSeriesName[] = ['cash', 'investment', 'equity', 'debt', 'netWorth', 'passive'];

export type CumulativeSeries = Readonly<Record<CumulativeSeriesName, Series>>;

/** The parts of a CashflowForecast the metrics read; missing series count as empty. */
export interface MetricsCashflow {
  readonly payments: Partial<Record<CashflowType, Series>>;
  readonly windowed: Partial<Record<CashflowType, Series>>;
}

export interface MetricsInputs {
  readonly cumulative: Partial<CumulativeSeries> & { readonly debtFreeDate?: Day | null };
  readonly cashflow: MetricsCashflow | null;
  readonly initialBalance: Decimal;
  /** null: current state falls back to the first buckets. */
  readonly transactions: readonly Transaction[] | null;
  readonly properties: readonly Property[];
  readonly assets: readonly Asset[];
  readonly depreciatingAssets: readonly DepreciatingAsset[];
  readonly expectedReturnPercent: Decimal;
  readonly forecastEndDate: Day | null;
  readonly cashSinceDate: Day | null;
  /** R18: the latest waypoint's investments on its date; null: investments are the modelled value. */
  readonly investmentBaseline: InvestmentBaseline | null;
  /** The forecast's own now (TIMELINE R19), when the metrics sit beside a line: the current fields are it. */
  readonly current?: Position;
}

export interface InvestmentBaseline {
  readonly investments: Decimal;
  readonly since: Day;
}

export const DEFAULT_METRICS_INPUTS: Omit<MetricsInputs, 'cumulative'> = {
  cashflow: null,
  initialBalance: ZERO,
  transactions: null,
  properties: [],
  assets: [],
  depreciatingAssets: [],
  expectedReturnPercent: dec(6),
  forecastEndDate: null,
  cashSinceDate: null,
  investmentBaseline: null,
};

/** ForecastMetrics. Every amount is round2ed except savingsRate. */
export interface ForecastMetrics {
  readonly currentNetWorth: Decimal;
  readonly currentCash: Decimal;
  readonly totalInvestments: Decimal;
  readonly totalDebt: Decimal;
  readonly currentPropertyEquity: Decimal;
  readonly monthlySavings: Decimal;
  readonly projectedPassiveIncome: Decimal;
  readonly finalNetWorth: Decimal;
  readonly finalCash: Decimal;
  readonly finalInvestment: Decimal;
  readonly finalDebt: Decimal;
  readonly finalPropertyEquity: Decimal;
  readonly finalPropertyValue: Decimal;
  readonly finalMortgageBalance: Decimal;
  readonly propertyPurchaseEquity: Decimal;
  readonly propertyGrowth: Decimal;
  readonly peakNetWorth: Decimal;
  readonly peakNetWorthDate: Day | null;
  readonly highestCash: Decimal;
  readonly highestCashDate: Day | null;
  readonly lowestCash: Decimal;
  readonly lowestCashDate: Day | null;
  readonly debtFreeDate: Day | null;
  readonly timeToDebtFreeMonths: number | null;
  readonly totalIncome: Decimal;
  readonly totalInvestmentContributions: Decimal;
  readonly totalExpenses: Decimal;
  readonly totalDebtPayments: Decimal;
  readonly netSavings: Decimal;
  readonly averagePassiveIncome: Decimal;
  /** (netSavings + totalInvestmentContributions) / totalIncome × 100 from the rounded fields; 0 without income. */
  readonly savingsRate: Decimal;
}

// MARK: Current cash

const activeOfType = (transactions: readonly Transaction[], type: TransactionType): readonly Transaction[] =>
  transactions.filter((t) => t.type === type && t.isActive);

/**
 * calculateTotalReceived: amount × occurrences in (since, date], round2ed. With an annual
 * increase each occurrence i is taken at its R11 amount on occurrenceDate(i).
 */
function totalReceived(t: Transaction, date: Day, since: Day | null): Decimal {
  const total = countOccurrencesUpTo(t, date);
  const excluded = since === null ? 0 : countOccurrencesUpTo(t, since);
  if (total - excluded <= 0) return ZERO;
  if (isZero(t.annualIncreasePercent) && t.history === undefined && t.actuals === undefined) {
    return round2(mul(t.amount, fromInt(total - excluded)));
  }
  let received: Decimal = ZERO;
  for (let i = excluded; i < total; i++) received = add(received, paidOn(t, occurrenceDate(t, i)));
  return round2(received);
}

/** Minus what a withdrawal paid into cash in (since, date]: only what its pot held (R23). */
const withdrawnSince = (t: Transaction, incomes: readonly Transaction[], returnPercent: Decimal, date: Day, since: Day | null): Decimal =>
  round2(sum(runPot(t, incomes, returnPercent, [], date).payments.filter((p) => since === null || p.date > since).map((p) => p.cash)));

/**
 * Metrics current cash (D5): initial + income − expenses − debt payments − the saver's own
 * investment contributions (never the match or relief, R13) up to `date` by the closed-form
 * count, round2ed.
 */
function currentCash(initialBalance: Decimal, transactions: readonly Transaction[], date: Day, since: Day | null, returnPercent: Decimal): Decimal {
  const incomes = activeOfType(transactions, 'income');
  const received = (type: TransactionType) =>
    activeOfType(transactions, type).map((t) => (type === 'investment' && lt(t.amount, ZERO) ? withdrawnSince(t, incomes, returnPercent, date, since) : totalReceived(t, date, since)));
  let cash = initialBalance;
  for (const amount of received('income')) cash = add(cash, amount);
  const debts = activeOfType(transactions, 'debt').map((t) => round2(totalPaid(t, addDays(date, 1), date, since)));
  for (const amount of [...received('expense'), ...debts, ...received('investment')]) cash = sub(cash, amount);
  return round2(cash);
}

// MARK: Legacy property equity

interface PropertyTotals {
  readonly equity: Decimal;
  readonly value: Decimal;
  readonly mortgageBalance: Decimal;
  readonly purchaseEquity: Decimal;
  readonly growth: Decimal;
}

/**
 * Bought by `date` and not sold: a sale counts from the start of its month, when the cashflow
 * books the proceeds (R2).
 */
const isHeld = (p: Property, date: Day): boolean => p.isActive && !(date < p.startDate) && !isSoldBy(p.saleEvent, date);

/**
 * Legacy property totals as the metrics compute them: appreciated value (R5) less the
 * re-amortised embedded mortgage (D8b), over the properties held at `date`.
 */
function propertyTotals(properties: readonly Property[], date: Day): PropertyTotals {
  let value: Decimal = ZERO;
  let mortgageBalance: Decimal = ZERO;
  let purchaseEquity: Decimal = ZERO;
  let growth: Decimal = ZERO;
  for (const p of properties) {
    if (!isHeld(p, date)) continue;
    const appreciated = appreciatedValue(p.propertyValue, p.annualAppreciationRate, p.startDate, date);
    const balance = reamortisedMortgageBalance(p.mortgage, date);
    const loan = p.mortgage.loanAmount;
    value = add(value, appreciated);
    mortgageBalance = add(mortgageBalance, balance);
    purchaseEquity = add(purchaseEquity, add(sub(p.propertyValue, loan), sub(loan, balance)));
    growth = add(growth, sub(appreciated, p.propertyValue));
  }
  return { equity: sub(value, mortgageBalance), value, mortgageBalance, purchaseEquity, growth };
}

/**
 * Current property equity, net of every mortgage (R3): each held legacy property less its
 * active linked debts when it has any, else less its embedded mortgage; then each unified
 * property asset not sold, less its active linked debts.
 */
function currentPropertyEquity(
  properties: readonly Property[],
  unifiedProperties: readonly Asset[],
  debts: readonly Transaction[],
  date: Day,
): Decimal {
  const owed = (assetId: Id): Decimal | null => {
    const linked = debts.filter((d) => d.isActive && d.linkedAssetId === assetId);
    return linked.length === 0 ? null : sum(linked.map((d) => abs(remainingBalance(d, date, date))));
  };
  let total: Decimal = ZERO;
  for (const p of properties) {
    if (!isHeld(p, date)) continue;
    const { equity, value } = propertyTotals([p], date);
    const linkedOwed = owed(p.id);
    total = add(total, linkedOwed === null ? equity : sub(value, linkedOwed));
  }
  for (const asset of unifiedProperties) {
    if (!isOwnedOn(asset, date)) continue;
    total = add(total, sub(assetValue(asset, date), owed(asset.id) ?? ZERO));
  }
  return total;
}

/** Non-property unified assets, then legacy depreciating assets not already among them, not sold, valued at `date` (D2a). */
function depreciatingAssetValue(assets: readonly Asset[], legacy: readonly DepreciatingAsset[], date: Day): Decimal {
  const counted = assets.filter((a) => a.isActive && !defaultsToAppreciation(a.assetClass));
  const ids = new Set(counted.map((a) => a.id));
  const converted = legacy.filter((a) => a.isActive && !ids.has(a.id)).map(assetFromDepreciatingAsset);
  return sum([...counted, ...converted].filter((a) => isOwnedOn(a, date)).map((a) => assetValue(a, date)));
}

// MARK: Series scans

const lastAmount = (series: Series): Decimal => series[series.length - 1]?.amount ?? ZERO;
const seriesTotal = (series: Series | undefined): Decimal => sum((series ?? []).map((b) => b.amount));

/** Strictly greater than the running best, starting from `floor` (null = the first bucket wins). */
function extreme(series: Series, better: (a: Decimal, b: Decimal) => boolean, floor: Decimal | null) {
  let best: { readonly amount: Decimal; readonly date: Day | null } | null = floor === null ? null : { amount: floor, date: null };
  for (const bucket of series) {
    if (best === null || better(bucket.amount, best.amount)) best = { amount: bucket.amount, date: bucket.end };
  }
  return best ?? { amount: ZERO, date: null };
}

/**
 * Debt free (R15): the first bucket at or below 0 after one above 0, dated by the forecast's
 * debt-free day when given, else the bucket's start; months = its 1-based index.
 */
function debtFree(debt: Series, debtFreeDay: Day | null): { readonly date: Day | null; readonly months: number | null } {
  const index = firstClearBucket(debt);
  return index === null ? { date: null, months: null } : { date: debtFreeDay ?? debt[index]!.start, months: index + 1 };
}

// MARK: Now

/** A position at the end of a day (TIMELINE "Position on day D"). */
export interface Position {
  readonly cash: Decimal;
  readonly investments: Decimal;
  /** The debt line: every debt not netted in property equity. */
  readonly debt: Decimal;
  readonly propertyEquity: Decimal;
  /** Non-property assets at their value. */
  readonly assetValue: Decimal;
  readonly netWorth: Decimal;
}

export type CurrentInputs = Omit<MetricsInputs, 'cumulative' | 'cashflow' | 'forecastEndDate' | 'transactions' | 'current'> & {
  readonly transactions: readonly Transaction[];
};

/**
 * The position at the end of the clock's day (TIMELINE R19): cash from its baseline (R17), investments
 * from theirs (R18), each moved by the flows dated after it; debts, property equity and other assets
 * from the plan.
 */
export function currentPosition(inputs: CurrentInputs, ctx: CalcContext): Position {
  const today = ctx.now;
  const legacyIds = new Set([...inputs.properties.map((p) => p.id), ...inputs.depreciatingAssets.map((a) => a.id)]);
  const unifiedAssets = inputs.assets.filter((a) => a.isActive && !legacyIds.has(a.id));
  const sold = saleDates(inputs.assets, inputs.properties, inputs.depreciatingAssets);
  const transactions = settleSoldAssetDebts(inputs.transactions, sold, { onlyDebts: true });
  const properties = new Set([
    ...inputs.assets.filter((a) => a.assetClass === 'property').map((a) => a.id),
    ...inputs.properties.map((p) => p.id),
  ]);
  const debtLine = transactions.filter((t) => t.type === 'debt' && countsAsDebt(t, properties));
  const settlements = saleSettlements(sold, debtLine);
  const unsettled = debtLine.filter((d) => {
    const saleDate = settlements.get(d.id);
    return saleDate === undefined || today < startOfMonth(saleDate);
  });
  const debtNow = totalCurrentDebt(unsettled, today, today);
  const modelledInvestments = (date: Day) => calculateTotalCurrentInvestments(
    activeOfType(transactions, 'investment'), date, inputs.expectedReturnPercent, transactions.filter((t) => t.type === 'income'),
  );
  // R18: the waypoint's investments, moved by what the plan models since its date (never backwards)
  const modelledNow = modelledInvestments(today);
  const baseline = inputs.investmentBaseline;
  const investmentsNow = baseline === null
    ? modelledNow
    : add(baseline.investments, sub(modelledNow, modelledInvestments(baseline.since < today ? baseline.since : today)));
  const cashNow = currentCash(inputs.initialBalance, transactions, today, inputs.cashSinceDate, inputs.expectedReturnPercent);
  const propertyNow = currentPropertyEquity(
    inputs.properties,
    unifiedAssets.filter((a) => a.assetClass === 'property'),
    transactions.filter((t) => t.type === 'debt'),
    today,
  );
  const assetsNow = depreciatingAssetValue(inputs.assets, inputs.depreciatingAssets, today);
  return {
    cash: cashNow,
    investments: investmentsNow,
    debt: debtNow,
    propertyEquity: propertyNow,
    assetValue: assetsNow,
    netWorth: add(add(sub(add(cashNow, investmentsNow), debtNow), propertyNow), assetsNow),
  };
}

// MARK: Metrics

export function calculateMetrics(inputs: MetricsInputs, ctx: CalcContext): ForecastMetrics {
  const today = ctx.now;
  const { cash = [], investment = [], debt = [], netWorth = [], passive = [] } = inputs.cumulative;
  const legacyIds = new Set([...inputs.properties.map((p) => p.id), ...inputs.depreciatingAssets.map((a) => a.id)]);
  const unifiedAssets = inputs.assets.filter((a) => a.isActive && !legacyIds.has(a.id));

  const now = inputs.current ?? (inputs.transactions === null ? null : currentPosition(inputs as CurrentInputs, ctx));
  const current = now ?? {
    netWorth: netWorth[0]?.amount ?? ZERO,
    cash: cash[0]?.amount ?? ZERO,
    investments: investment[0]?.amount ?? ZERO,
    debt: debt[0]?.amount ?? ZERO,
  };
  const propertyNow = now?.propertyEquity ?? currentPropertyEquity(inputs.properties, unifiedAssets.filter((a) => a.assetClass === 'property'), [], today);
  const currentNetWorth = current.netWorth;

  const finalProperty = propertyTotals(inputs.properties, inputs.forecastEndDate ?? addYears(today, 10));
  const peak = extreme(netWorth, gt, ZERO);
  const highest = extreme(cash, gt, null);
  const lowest = extreme(cash, lt, null);
  const debtFreeAt = debtFree(debt, inputs.cumulative.debtFreeDate ?? null);

  const payments = inputs.cashflow?.payments ?? {};
  const totalIncome = seriesTotal(payments.income);
  const totalInvestmentContributions = seriesTotal(payments.investment);
  const totalExpenses = seriesTotal(payments.expense);
  const totalDebtPayments = seriesTotal(payments.debt);
  const netSavings = sub(sub(sub(totalIncome, totalExpenses), totalDebtPayments), totalInvestmentContributions);

  const windowed = inputs.cashflow?.windowed ?? {};
  const firstOf = (type: CashflowType): Decimal => windowed[type]?.[0]?.amount ?? ZERO;
  const legacyPropertyCosts = sum(inputs.properties.filter((p) => p.isActive).map(propertyMonthlyCosts));
  const monthlySavings = sub(sub(sub(firstOf('income'), firstOf('expense')), firstOf('debt')), legacyPropertyCosts);

  const averagePassiveIncome = passive.length === 0 ? ZERO : div(seriesTotal(passive), fromInt(passive.length));

  const rounded = {
    totalIncome: round2(totalIncome),
    totalInvestmentContributions: round2(totalInvestmentContributions),
    netSavings: round2(netSavings),
  };
  const savingsRate = gt(rounded.totalIncome, ZERO)
    ? mul(div(add(rounded.netSavings, rounded.totalInvestmentContributions), rounded.totalIncome), dec(100))
    : ZERO;

  return {
    currentNetWorth: round2(currentNetWorth),
    currentCash: round2(current.cash),
    totalInvestments: round2(current.investments),
    totalDebt: round2(current.debt),
    currentPropertyEquity: round2(propertyNow),
    monthlySavings: round2(monthlySavings),
    projectedPassiveIncome: round2(averagePassiveIncome),
    finalNetWorth: round2(lastAmount(netWorth)),
    finalCash: round2(lastAmount(cash)),
    finalInvestment: round2(lastAmount(investment)),
    finalDebt: round2(lastAmount(debt)),
    finalPropertyEquity: round2(finalProperty.equity),
    finalPropertyValue: round2(finalProperty.value),
    finalMortgageBalance: round2(finalProperty.mortgageBalance),
    propertyPurchaseEquity: round2(finalProperty.purchaseEquity),
    propertyGrowth: round2(finalProperty.growth),
    peakNetWorth: round2(peak.amount),
    peakNetWorthDate: peak.date,
    highestCash: round2(highest.amount),
    highestCashDate: highest.date,
    lowestCash: round2(lowest.amount),
    lowestCashDate: lowest.date,
    debtFreeDate: debtFreeAt.date,
    timeToDebtFreeMonths: debtFreeAt.months,
    totalIncome: rounded.totalIncome,
    totalInvestmentContributions: rounded.totalInvestmentContributions,
    totalExpenses: round2(totalExpenses),
    totalDebtPayments: round2(totalDebtPayments),
    netSavings: rounded.netSavings,
    averagePassiveIncome: round2(averagePassiveIncome),
    savingsRate,
  };
}
